import { useCallback, useEffect, useState } from 'react';
import { t } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { Button, Dialog, Field } from './ui.tsx';

/**
 * Fields a business added to its own records (Settings → Fields of your own). A form asks for
 * them with `useCustomFields`, shows them with `CustomFieldInputs` and saves them right after the
 * record itself; detail pages show them with `CustomFieldsView`, and lists add a column for each
 * field marked "show in the list" with `useCustomColumns`.
 */

export type CustomTarget = 'customer.customer' | 'product.product' | 'purchase.supplier' | 'invoicing.invoice' | 'crm.lead' | 'construction.project' | 'payroll.employee';
export type CustomKind = 'text' | 'long_text' | 'number' | 'money' | 'date' | 'yes_no' | 'choice';
export interface CustomField {
  id: string;
  target: CustomTarget;
  key: string;
  label: string;
  kind: CustomKind;
  choices: string[] | null;
  required: boolean;
  in_list: boolean;
  help: string | null;
  sequence: number;
  active: boolean;
}
export type CustomValue = string | number | boolean;
export type CustomValues = Record<string, CustomValue>;

/** The fields of one kind of record, loaded once per page. */
function useFieldDefs(target: CustomTarget) {
  const { can } = useSession();
  const on = can('studio.use');
  const [fields, setFields] = useState<CustomField[] | null>(on ? null : []);
  const [canEdit, setCanEdit] = useState(false);
  useEffect(() => {
    if (!on) return;
    let live = true;
    action<{ fields: CustomField[]; can_edit: boolean }>('studio.fields', { target }).then(
      (r) => {
        if (!live) return;
        setFields(r.fields);
        setCanEdit(r.can_edit);
      },
      // Someone who cannot see these records has no fields to fill in.
      () => live && setFields([]),
    );
    return () => {
      live = false;
    };
  }, [on, target]);
  return { fields, canEdit };
}

/** What the person types, kept as text until it is saved. */
type Draft = Record<string, string | boolean>;

function toDraft(f: CustomField, v: CustomValue | undefined): string | boolean {
  if (f.kind === 'yes_no') return v === true;
  if (v === undefined) return '';
  if (f.kind === 'money') return (Number(v) / 100).toFixed(2).replace(/\.00$/, '');
  return String(v);
}

function fromDraft(f: CustomField, d: string | boolean): { value: CustomValue | null; bad: boolean } {
  if (f.kind === 'yes_no') return { value: d === true, bad: false };
  const s = String(d).trim();
  if (!s) return { value: null, bad: false };
  if (f.kind === 'number') {
    const n = Number(s.replace(',', '.'));
    return Number.isFinite(n) ? { value: n, bad: false } : { value: null, bad: true };
  }
  if (f.kind === 'money') {
    const n = parseMoney(s);
    return n === null ? { value: null, bad: true } : { value: n, bad: false };
  }
  return { value: s, bad: false };
}

/**
 * The fields of a record in a form. `record` is null for a new record: `save(id)` is then
 * called with the id the record got.
 */
export function useCustomFields(target: CustomTarget, record: string | null | undefined) {
  const { fields, canEdit } = useFieldDefs(target);
  const { fail } = useUi();
  const [draft, setDraft] = useState<Draft>({});
  const [ready, setReady] = useState(!record);
  useEffect(() => {
    if (!fields) return;
    if (!record || !fields.length) {
      setDraft(Object.fromEntries(fields.map((f) => [f.key, toDraft(f, undefined)])));
      setReady(true);
      return;
    }
    let live = true;
    action<Record<string, CustomValues>>('studio.values', { target, records: [record] }).then(
      (r) => {
        if (!live) return;
        const v = r[record] ?? {};
        setDraft(Object.fromEntries(fields.map((f) => [f.key, toDraft(f, v[f.key])])));
        setReady(true);
      },
      () => live && setReady(true),
    );
    return () => {
      live = false;
    };
  }, [fields, record, target]);

  const list = fields ?? [];
  const errors: Record<string, string> = {};
  for (const f of list) {
    const { value, bad } = fromDraft(f, draft[f.key] ?? '');
    if (bad) errors[f.key] = t('studio.bad_value');
    else if (f.required && f.kind !== 'yes_no' && value === null) errors[f.key] = t('form.required');
  }
  const editable = canEdit && ready;
  const save = useCallback(
    async (id: string) => {
      if (!editable || !list.length) return true;
      const values = Object.fromEntries(list.map((f) => [f.key, fromDraft(f, draft[f.key] ?? '').value]));
      try {
        await action('studio.set_values', { target, record: id, values });
        return true;
      } catch (err) {
        fail(err);
        return false;
      }
    },
    [editable, list, draft, target, fail],
  );
  return {
    fields: list,
    draft,
    set: (key: string, v: string | boolean) => setDraft((d) => ({ ...d, [key]: v })),
    errors,
    /** False while a value is missing or does not look right. */
    valid: !editable || !Object.keys(errors).length,
    editable,
    save,
  };
}

export type CustomFieldsState = ReturnType<typeof useCustomFields>;

/** The inputs for the fields of a record, laid out like the rest of the form. */
export function CustomFieldInputs({ cf, tried, bare }: { cf: CustomFieldsState; tried?: boolean; /** Without the "More details" heading, when the dialog is only these fields. */ bare?: boolean }) {
  if (!cf.fields.length || !cf.editable) return null;
  return (
    <fieldset className={bare ? 'cf-set cf-bare' : 'cf-set'}>
      {bare ? null : <legend className="cf-legend">{t('studio.more_details')}</legend>}
      <div className="form-grid">
        {cf.fields.map((f) => {
          const v = cf.draft[f.key] ?? '';
          const error = tried || (f.kind !== 'yes_no' && v !== '' && cf.errors[f.key] === t('studio.bad_value')) ? (cf.errors[f.key] ?? null) : null;
          if (f.kind === 'yes_no') {
            return (
              <label key={f.key} className="check-row cf-check">
                <input type="checkbox" checked={v === true} onChange={(e) => cf.set(f.key, e.target.checked)} />
                <span>
                  <span className="strong">{f.label}</span>
                  {f.help ? <span className="muted">{f.help}</span> : null}
                </span>
              </label>
            );
          }
          return (
            <div key={f.key} className={f.kind === 'long_text' ? 'cf-wide' : undefined}>
              <Field label={f.label} hint={f.help ?? undefined} error={error} required={f.required}>
                {f.kind === 'choice' ? (
                  <select className="input" value={String(v)} onChange={(e) => cf.set(f.key, e.target.value)}>
                    <option value="">{f.required ? t('studio.choose') : t('studio.none')}</option>
                    {/* A value kept from an option that was taken off the list still shows. */}
                    {[...(f.choices ?? []), ...(v && !(f.choices ?? []).includes(String(v)) ? [String(v)] : [])].map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                ) : f.kind === 'long_text' ? (
                  <textarea className="input" rows={3} maxLength={4000} value={String(v)} onChange={(e) => cf.set(f.key, e.target.value)} />
                ) : f.kind === 'date' ? (
                  <input className="input" type="date" value={String(v)} onChange={(e) => cf.set(f.key, e.target.value)} />
                ) : f.kind === 'number' || f.kind === 'money' ? (
                  <input className="input tabular" inputMode="decimal" value={String(v)} onChange={(e) => cf.set(f.key, e.target.value)} />
                ) : (
                  <input className="input" maxLength={200} value={String(v)} onChange={(e) => cf.set(f.key, e.target.value)} />
                )}
              </Field>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

/** One value as people read it. */
export function customText(f: Pick<CustomField, 'kind'>, v: CustomValue | undefined): string {
  if (v === undefined || v === '') return '—';
  switch (f.kind) {
    case 'yes_no':
      return v ? t('studio.yes') : t('studio.no');
    case 'money':
      return money(Number(v));
    case 'date':
      return day(String(v));
    case 'number':
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(Number(v));
    default:
      return String(v);
  }
}

/**
 * The fields of a record on its detail page. People who may change the record can change them
 * here too, also once the record itself can no longer be edited (an issued invoice). Shows
 * nothing when the business has no fields for these records.
 */
export function CustomFieldsView({
  target,
  record,
  version,
  title,
  className,
}: {
  target: CustomTarget;
  record: string;
  /** Anything that changes when the record was saved again, to read the values afresh. */
  version?: unknown;
  title?: string;
  className?: string;
}) {
  const { fields, canEdit } = useFieldDefs(target);
  const [values, setValues] = useState<CustomValues | null>(null);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(0);
  useEffect(() => {
    if (!fields?.length) return;
    let live = true;
    action<Record<string, CustomValues>>('studio.values', { target, records: [record] }).then(
      (r) => live && setValues(r[record] ?? {}),
      () => live && setValues({}),
    );
    return () => {
      live = false;
    };
  }, [fields, target, record, version, saved]);
  if (!fields?.length || !values) return null;
  const heading = title ?? t('studio.more_details');
  return (
    <section className={`panel cf-view${className ? ` ${className}` : ''}`} aria-label={heading}>
      <div className="panel-head">
        <h2>{heading}</h2>
        {canEdit ? (
          <Button variant="ghost" size="sm" icon="edit" onClick={() => setEditing(true)}>
            {t('studio.edit_values')}
          </Button>
        ) : null}
      </div>
      <dl className="cf-dl">
        {fields.map((f) => (
          <div key={f.key} className={f.kind === 'long_text' ? 'cf-wide' : undefined}>
            <dt>{f.label}</dt>
            <dd className={values[f.key] === undefined ? 'muted' : f.kind === 'money' || f.kind === 'number' ? 'tabular' : undefined}>{customText(f, values[f.key])}</dd>
          </div>
        ))}
      </dl>
      {editing ? (
        <EditValues
          target={target}
          record={record}
          title={heading}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setSaved((n) => n + 1);
          }}
        />
      ) : null}
    </section>
  );
}

function EditValues({ target, record, title, onClose, onSaved }: { target: CustomTarget; record: string; title: string; onClose: () => void; onSaved: () => void }) {
  const cf = useCustomFields(target, record);
  const { toast } = useUi();
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setTried(true);
    if (!cf.valid) return;
    setBusy(true);
    const ok = await cf.save(record);
    setBusy(false);
    if (ok) {
      toast(t('studio.values_saved'), 'ok');
      onSaved();
    }
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <CustomFieldInputs cf={cf} tried={tried} bare />
    </Dialog>
  );
}

/** Columns for the fields marked "show in the list", with the values of the rows shown. */
export function useCustomColumns(target: CustomTarget, ids: readonly string[]) {
  const { fields } = useFieldDefs(target);
  const columns = (fields ?? []).filter((f) => f.in_list);
  const [values, setValues] = useState<Record<string, CustomValues>>({});
  const key = columns.length ? ids.slice(0, 500).join(',') : '';
  useEffect(() => {
    if (!key) return;
    let live = true;
    action<Record<string, CustomValues>>('studio.values', { target, records: key.split(',') }).then(
      (r) => live && setValues(r),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [key, target]);
  return {
    columns,
    /** The value as text, for a table cell. */
    cell: (f: CustomField, id: string) => <span className="cf-cell">{customText(f, values[id]?.[f.key])}</span>,
    /** "Industry: Hotel · Rooms: 40" for a list row, leaving out the empty ones. */
    line: (id: string) =>
      columns
        .filter((f) => values[id]?.[f.key] !== undefined)
        .map((f) => `${f.label}: ${customText(f, values[id]![f.key])}`)
        .join(' · '),
  };
}
